import React, {FormEvent, ReactNode} from "react";
import {CheckboxItem, Icon, styled, theme, Text} from "@reside/ui";
import {
  PermissionPendingSuffix,
  PermissionStatusGroup,
} from "./PermissionPendingSuffix";

type Props = Readonly<{
  label: ReactNode;
  selectedCount: number;
  totalCount: number;
  someChecked: boolean;
  everyChecked: boolean;
  disabled?: boolean;
  isPending?: boolean;
  headerActions?: ReactNode;
  onGroupChange: (event: FormEvent<HTMLInputElement>) => void;
  children: ReactNode;
}>;

export const PermissionCategoryCard = ({
  label,
  selectedCount,
  totalCount,
  someChecked,
  everyChecked,
  disabled = false,
  isPending = false,
  headerActions,
  onGroupChange,
  children,
}: Props) => (
  <Card>
    <Header>
      <HeaderMain>
        <HeaderCheckbox
          label={<HeaderLabel>{label}</HeaderLabel>}
          checked={someChecked}
          Icon={props =>
            someChecked && !everyChecked ? (
              <Icon {...props} name="minus-1" />
            ) : (
              <Icon {...props} />
            )
          }
          disabled={disabled}
          onChange={onGroupChange}
        />
        {headerActions}
      </HeaderMain>
      <PermissionStatusGroup>
        <CountBadge>
          {selectedCount} of {totalCount}
        </CountBadge>
        {isPending ? <PermissionPendingSuffix /> : null}
      </PermissionStatusGroup>
    </Header>
    <Body>{children}</Body>
  </Card>
);

export const PermissionCategoryItem = styled.div`
  border-top: 1px solid ${theme.color.gray10};
  padding: 8px 16px;

  label {
    align-items: center;
    display: flex;
    margin: 0;
    width: 100%;
  }

  /* CheckboxItem renders label text in an inner span with font-size: 1.25em */
  label > span {
    font-size: 16px;
    line-height: 20px;
    margin-top: 0;
  }
`;

const Card = styled.div`
  border: 1px solid ${theme.color.gray10};
  border-radius: 8px;
  overflow: hidden;
`;

const Header = styled.div`
  align-items: center;
  background: #f0f5ff;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 8px 16px;
`;

const HeaderMain = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 12px;
  min-width: 0;
`;

const HeaderCheckbox = styled(CheckboxItem)`
  flex: 1;
  min-width: 0;

  label {
    align-items: center;
    display: flex;
    margin: 0;
  }

  label > span {
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    margin-top: 0;
  }
`;

const HeaderLabel = styled(Text)`
  color: ${theme.color.darkBlue100};
`;

const CountBadge = styled.span`
  background: ${theme.color.primary};
  border-radius: 999px;
  color: #ffffff;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  padding: 4px 10px;
  white-space: nowrap;
`;

const Body = styled.div`
  background: #ffffff;
`;
